/**
 * useSpotPositions Hook
 * 
 * Calculates position data for experience spots based on element selectors
 */

import { useCallback } from 'react';
import { ExperienceSpot } from '../../../types/experienceMode.types';

export const useSpotPositions = () => {
  /**
   * Calculate positions for all spots
   */
  const calculatePositions = useCallback((spots: ExperienceSpot[]): ExperienceSpot[] => {
    return spots.map(spot => {
      try {
        // Skip if no selector provided
        if (!spot.elementSelector) {
          return spot;
        }
        
        const element = document.querySelector(spot.elementSelector);
        
        if (!element) {
          console.warn(`Element not found for selector: ${spot.elementSelector}`);
          return spot;
        }
        
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        // Calculate element position (for highlight cutout)
        const position = {
          top: `${rect.top + scrollTop}px`,
          left: `${rect.left + scrollLeft}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        };
        
        // Calculate icon position (top-right corner of element)
        const iconPosition = {
          top: `${rect.top + scrollTop - 12}px`, // Slightly above
          left: `${rect.left + scrollLeft + rect.width - 12}px`, // Slightly to the right
        };
        
        return {
          ...spot,
          position,
          iconPosition,
        };
      } catch (error) {
        console.error(`Error calculating position for spot ${spot.id}:`, error);
        return spot;
      }
    });
  }, []);

  /**
   * Calculate position for a single spot
   */
  const calculateSinglePosition = useCallback((spot: ExperienceSpot): ExperienceSpot => {
    const updated = calculatePositions([spot]);
    return updated[0] || spot;
  }, [calculatePositions]);

  return {
    calculatePositions,
    calculateSinglePosition,
  };
};
